import {
  listQueue,
  loadContext,
  recordAttempt,
  removeFromQueue,
} from './db.js';
import type { QueuedItem } from './db.js';

/**
 * Draining the offline queue.
 *
 * ## How a background send is authenticated
 *
 * Every `/v1` route needs a company access token, and those last fifteen
 * minutes. A queue drained hours after the photo was taken therefore cannot
 * reuse whatever token was current at the time — which is why the queue stores
 * intents rather than requests (see db.ts).
 *
 * What *is* long-lived is the Better Auth session cookie: thirty days, httpOnly,
 * sent by the browser rather than read by this code. So the first thing a drain
 * does is exchange that cookie for a fresh company token at
 * `POST /v1/auth/session/company`, exactly as the application does at start-up.
 * This works in a service worker for the same reason it works in a tab: the
 * cookie is attached by the browser when `credentials: 'include'` is set.
 *
 * If the person has signed out, that exchange fails with 401 and the drain
 * stops without touching the queue. The receipts stay where they are until
 * somebody signs in again, which is the only correct answer — they are not this
 * code's to discard.
 *
 * ## Why this module knows nothing about React
 *
 * It is imported by the service worker as well as by the page. Nothing here may
 * touch `window`, `document` or any DOM type.
 */

/**
 * How many times an item is retried before it is set aside.
 *
 * A queue is drained in order, so one item the server will never accept — a
 * photo of a file type the API refuses, a trip dated before the rates begin —
 * would otherwise block every receipt behind it, for ever, invisibly. After
 * five attempts the item stops being retried automatically; it stays in the
 * queue, it is shown on the page with the server's own reason, and a person
 * decides.
 */
export const MAX_ATTEMPTS = 5;

export interface DrainResult {
  readonly sent: number;
  readonly failed: number;
  readonly remaining: number;
  /** Set when the drain could not start at all, e.g. nobody is signed in. */
  readonly blocked?: string | undefined;
}

interface TokenResponse {
  readonly accessToken: string;
}

async function mintToken(apiBaseUrl: string, companyId: string): Promise<string> {
  const response = await fetch(`${apiBaseUrl}/v1/auth/session/company`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? 'not signed in'
        : `could not get an access token (HTTP ${String(response.status)})`,
    );
  }
  const payload = (await response.json()) as TokenResponse;
  return payload.accessToken;
}

function requestFor(item: QueuedItem): { path: string; body: unknown } {
  if (item.payload.kind === 'attachment') {
    return {
      path: '/v1/attachments',
      body: {
        filename: item.payload.filename,
        mimeType: item.payload.mimeType,
        contentBase64: item.payload.contentBase64,
        // Recorded as having come from the UI, which is true: the queue is a
        // delivery mechanism, not a channel. Inventing an "offline" channel
        // would put a transport detail into a five-year retention record.
        channel: 'ui',
      },
    };
  }
  return {
    path: '/v1/trips',
    body: {
      date: item.payload.date,
      startAddress: item.payload.startAddress,
      endAddress: item.payload.endAddress,
      distanceKm: item.payload.distanceKm,
      purpose: item.payload.purpose,
      isBusiness: item.payload.isBusiness,
    },
  };
}

/**
 * Decide what a failed attempt means.
 *
 * A 4xx other than 401/408/429 is a refusal: the request is wrong and sending
 * it again changes nothing, so the item is counted as attempted and will stop
 * being retried. Everything else — the network being down, a 5xx, a rate limit
 * — is a *this moment* problem, and the item is left untouched so it does not
 * burn through its attempts while the server is simply restarting.
 */
function isPermanent(status: number): boolean {
  if (status === 401 || status === 408 || status === 429) {
    return false;
  }
  return status >= 400 && status < 500;
}

/**
 * Send everything that is waiting, for every company it was captured in.
 *
 * ## Every company, not the selected one
 *
 * Items are captured in whichever company was selected at the time, and the
 * person may well have switched since. The first version drained only the
 * current company's items, so two receipts photographed offline in one company
 * sat unsent -- and disappeared from the notice -- the moment the switcher moved.
 * Each company's items are sent under a token minted for that company, so an
 * item is never filed into books other than the ones it was captured for.
 *
 * A company this person can no longer get a token for (signed out, membership
 * revoked) is skipped with its reason reported, and does not stop the others.
 *
 * ## Stopping on a dead network
 *
 * The drain stops at the first transient failure: the network is either there
 * or it is not, and hammering thirty queued photos against a dead connection
 * wastes battery on a phone that is already somewhere with no signal.
 */
export async function drainQueue(): Promise<DrainResult> {
  const context = await loadContext();
  if (context === undefined) {
    return { sent: 0, failed: 0, remaining: 0, blocked: 'no company selected' };
  }

  const all = await listQueue();
  const pending = all.filter((item) => item.attempts < MAX_ATTEMPTS);
  if (pending.length === 0) {
    return { sent: 0, failed: 0, remaining: all.length };
  }

  // Grouped in queue order, so each company's oldest item is still sent first.
  const byCompany = new Map<string, QueuedItem[]>();
  for (const item of pending) {
    const group = byCompany.get(item.companyId) ?? [];
    group.push(item);
    byCompany.set(item.companyId, group);
  }

  let sent = 0;
  let failed = 0;
  let blocked: string | undefined;
  let networkDown = false;

  for (const [companyId, items] of byCompany) {
    let token: string;
    try {
      token = await mintToken(context.apiBaseUrl, companyId);
    } catch (error) {
      blocked ??= error instanceof Error ? error.message : String(error);
      if (error instanceof TypeError) {
        // fetch itself failed: no network, so no other company will fare better.
        break;
      }
      continue;
    }

    for (const item of items) {
      const outcome = await sendOne(context.apiBaseUrl, token, item);
      if (outcome === 'sent') {
        sent += 1;
      } else if (outcome === 'refused') {
        failed += 1;
      } else {
        networkDown = true;
        break;
      }
    }
    if (networkDown) {
      break;
    }
  }

  return {
    sent,
    failed,
    remaining: (await listQueue()).length,
    ...(blocked === undefined ? {} : { blocked }),
  };
}

type SendOutcome = 'sent' | 'refused' | 'transient';

async function sendOne(apiBaseUrl: string, token: string, item: QueuedItem): Promise<SendOutcome> {
  const { path, body } = requestFor(item);
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        // The same key on every attempt: this is what makes a retry after a
        // lost response safe rather than a second copy of the receipt.
        'idempotency-key': item.idempotencyKey,
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Unreachable. Leave the item exactly as it is.
    return 'transient';
  }

  if (response.ok) {
    await removeFromQueue(item.id);
    return 'sent';
  }

  const text = await response.text().catch(() => '');
  let reason = `HTTP ${String(response.status)}`;
  try {
    const envelope = JSON.parse(text) as { message?: string };
    if (typeof envelope.message === 'string') {
      reason = envelope.message;
    }
  } catch {
    // Not the API's error envelope. The status is what there is to report.
  }

  if (isPermanent(response.status)) {
    await recordAttempt(item.id, reason);
    return 'refused';
  }
  // Transient: do not spend an attempt, and stop trying for now.
  return 'transient';
}
